import { Box, Button, Typography } from '@mui/material'
import React from 'react'
import Slider from 'react-slick'
import { AdoptersClient } from '../../interactors/clients/AdoptersClient'
import { AnimalDtoSpecies } from '../../interactors/gen/backendClient'
import { colors, theme } from '../theme'
import { PromoButton } from './PromoButton'

interface Offer {
  id: string
  tipTitle: string
  tipDescription: string
  title: string
  imageUrl: string
  link: string
  description: string[]
  offer: string
  buttonText: string
  promoCode?: string
  species?: AnimalDtoSpecies[]
  promoText?: string
}

interface Props {
  animalName: string
  animalId: string
  species: AnimalDtoSpecies
}

export const AdoptionEndOffers: React.FC<Props> = ({ animalName, animalId, species }) => {
  const offersToDisplay: Offer[] = [
    {
      id: 'kozoo',
      title: 'L’assurance Kozoo :',
      tipTitle: 'Adoptez les bons réflexes',
      tipDescription:
        'Face à l’augmentation des frais vétérinaires, Protégez votre animal, maîtrisez votre budget santé.',
      imageUrl: 'https://i.imgur.com/pt5mTQ5.jpeg',
      link: 'https://app.kozoo.eu/register?utm_source=Petso&utm_medium=CPC&utm_campaign=Petso',
      description: [
        '48 offres sur-mesure',
        'Dès 5,15€/mois',
        'Sans feuille de soin',
        'Remboursement sous 48h',
        'Téléconseil illimité 24/24h',
        'Utilisable dès 15 jours',
      ],
      offer: 'Bénéficiez de 1 mois offert avec Petso',
      buttonText: 'Demander un devis gratuit',
      promoCode: 'PETSO1',
    },
    {
      id: 'caats',
      title: "Le kit d'essai Caats :",
      tipTitle: 'Adoptez les bons réflexes',
      tipDescription: ' Une bonne nutrition, c’est la base de sa santé et de sa vitalité au quotidien',
      imageUrl: 'https://i.imgur.com/uF3GoNA.png',
      link: 'https://app.caats.co/formulaire/check?coupon=PETSO',
      description: [
        'Un menu adapté à ses besoins',
        'Approuvé par + 40 000 chats',
        'Livré directement chez vous',
        'Fait par des vétérinaires',
        'Fabriqué en France',
      ],
      offer: 'Bénéficiez de 2 semaines offertes avec Petso',
      buttonText: "Obtenir mon kit d'essai gratuit",
      promoText: '2 semaines offertes puis abonnement sans engagement',
      species: [AnimalDtoSpecies.Cat],
    },
    {
      id: 'litiere-tranquille',
      title: 'Litière Tranquille :',
      tipTitle: 'Adoptez les bons réflexes',
      tipDescription:
        'Privilégiez une hygiène irréprochable.\nPour son confort, votre tranquillité, et un foyer toujours propre.',
      imageUrl: 'https://i.imgur.com/PKgj3NT.png',
      link: 'https://litiere-tranquille.com/?ae=58',
      description: ['Absorbante & écologique', 'Sans conservateurs', 'Elimine les odeurs', 'Fabriquée en France'],
      offer: '15% sur votre abonnement avec Petso',
      buttonText: 'Commander ma litière',
      species: [AnimalDtoSpecies.Cat],
    },
  ]

  const filteredOffers = offersToDisplay.filter((offer) => {
    // no species filter ⇒ show everything
    if (!species) return true

    // offer.species can be a string or an array
    const s = offer.species
    if (!s) return true // no species on offer ⇒ show it
    if (Array.isArray(s)) {
      return s.includes(species) // array ⇒ check inclusion
    }
    return s === species // string ⇒ exact match
  })

  const settings = {
    dots: true,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: filteredOffers.length > 1, // only enable infinite if more than one offer
    initialSlide: 0,
    speed: 200,
    arrows: false,
    customPaging: () => <div className="ft-slick__dots--custom"></div>,
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginX: -2 }}>
      <div style={{ height: '20px', width: '100%', backgroundColor: 'rgba(239, 246, 252, 1)' }} />
      <Box sx={{ width: '100%', py: 2 }}>
        <Slider {...settings}>
          {filteredOffers.map((offer) => (
            <Card key={offer.id} offer={offer} animalName={animalName} animalId={animalId} />
          ))}
        </Slider>
      </Box>

      <div style={{ height: '20px', width: '100%', backgroundColor: 'rgba(239, 246, 252, 1)' }} />
    </Box>
  )
}

interface CardProps {
  offer: Offer
  animalName: string
  animalId: string
}

const Card: React.FC<CardProps> = ({ offer, animalName, animalId }) => {
  const onSubmit = () => {
    AdoptersClient.sendMonitoringRequest({
      message: `Clique reçu sur l'offre ${offer.id}`,
      animalName: animalName,
      animalId: animalId,
    })
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onSubmit()
    window.open(offer.link, '_blank', 'noopener,noreferrer')
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2, pt: 0 }}>
      <Typography
        sx={{ color: colors.blueSky, textAlign: 'left', width: '100%', fontSize: '20px', fontWeight: 'bold' }}
      >
        {offer.tipTitle}
      </Typography>
      <Typography
        sx={{
          color: theme.palette.secondary.main,
          textAlign: 'left',
          width: '100%',
          fontSize: '15px',
          whiteSpace: 'pre-line', // ← this makes “\n” render as line breaks
        }}
      >
        {offer.tipDescription}
      </Typography>

      <h4
        style={{
          color: theme.palette.secondary.main,
          textAlign: 'left',
          width: '100%',
          fontSize: '18px',
          paddingBottom: 0,
          marginBottom: '8px',
        }}
      >
        {offer.title}
      </h4>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <img
          src={offer.imageUrl}
          alt={offer.title}
          style={{ width: '40%', height: '100px', borderRadius: '8px', objectFit: 'contain' }}
        />
        <ul style={{ width: '60%', listStyleType: 'none', paddingLeft: 0, margin: 0, fontSize: '0.8rem' }}>
          {offer.description.map((desc, index) => (
            <li key={index}>👉 {desc}</li>
          ))}
        </ul>
      </Box>
      <h3 style={{ color: theme.palette.secondary.main, fontSize: '16px' }}>{offer.offer}</h3>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: colors.blueSky }}>
        {offer.promoCode && (
          <PromoButton promoCode={offer.promoCode} offerId={offer.id} animalName={animalName} animalId={animalId} />
        )}
        <Button
          variant="contained"
          sx={{
            color: 'white',
            backgroundColor: `${colors.blueSky} !important`,
            padding: '8px 16px',
            borderRadius: '24px',
            fontWeight: 'bold',
            fontSize: '1rem',
          }}
          onClick={handleClick}
        >
          {offer.buttonText}
        </Button>
        {offer.promoText && (
          <p
            style={{ fontSize: '0.8rem', textAlign: 'center', marginTop: '16px', color: theme.palette.secondary.main }}
          >
            {offer.promoText}
          </p>
        )}
      </Box>
    </Box>
  )
}
